import React, { useCallback, useMemo, useState } from "react";
import { createEnumParam, useQueryParam } from "use-query-params";
import { getCheckDetailExpandedState } from "@common/helpers/domains/accounting/checkDetailRows";
import { CheckDetailGroupBy } from "@common/types/reports/check-detail";
import { View } from "@common/types/views/views";
import {
    CheckDetailView,
    CheckDetailViewSchema,
} from "@common/types/views/checkDetailView";
import { useCheckDetailQuery } from "@api/accountingReports.api";
import { Button } from "@design-system/Button/Button";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { GroupBySelect } from "@design-system/ViewTable/grouping/GroupBySelect";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { isCheckingAccount } from "@helpers/financialAccount";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { CollapseIcon, ExpandIcon } from "@icons";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import {
    GENERAL_LEDGER_PDF_TOO_LARGE_TOOLTIP,
    getGeneralLedgerDownloadMenuState,
} from "../generalLedger/generalLedgerDownloadMenu";
import { useAccountingTabContext } from "../useAccountingTab";
import { useExpandAllState } from "../useExpandAllState";
import { CheckDetailContent } from "./CheckDetailContent";
import {
    CHECK_DETAIL_GROUP_BY_OPTIONS,
    CHECK_DETAIL_GROUP_BY_VALUES,
} from "./checkDetailViewBuilder";
import {
    buildCheckDetailConfig,
    buildCheckDetailView,
    getCheckDetailVisibleColumns,
} from "./checkDetailViewConfig";
import { useCheckDetailDownload } from "./useCheckDetailDownload";

const GroupByParam = createEnumParam(CHECK_DETAIL_GROUP_BY_VALUES);

export const CheckDetailPage: React.FC = () => {
    const { filters, setFilters, selectedEntity } = useAccountingTabContext();
    const businessEntityIds = useBusinessNonMockEntityIds();
    const isClassesEnabled = useIsClassesEnabled();
    const [groupByParam, setGroupByParam] = useQueryParam(
        "groupBy",
        GroupByParam,
    );
    const groupBy: CheckDetailGroupBy = groupByParam ?? "bankAccount";

    const selectedEntityIds = useMemo(() => {
        if (filters.entityIds && filters.entityIds.length > 0) {
            return filters.entityIds;
        }
        if (selectedEntity) {
            return [selectedEntity.id];
        }
        return businessEntityIds;
    }, [filters.entityIds, selectedEntity, businessEntityIds]);

    const reportFilters = useMemo(
        () => ({ ...filters, entityIds: selectedEntityIds }),
        [filters, selectedEntityIds],
    );

    const { checkDetail, generationDate } = useCheckDetailQuery({
        filters,
        entityIds: selectedEntityIds,
    });

    const defaultView = useMemo(() => buildCheckDetailView(), []);
    const numberFormat = checkDetail.data?.presentation.numberFormat;
    const config = useMemo(
        () =>
            buildCheckDetailConfig({
                multiEntity: selectedEntityIds.length > 1,
                numberFormat,
                isClassesEnabled,
            }),
        [selectedEntityIds.length, numberFormat, isClassesEnabled],
    );

    const [cachedView, setCachedView] = useWorkspaceUserCache<CheckDetailView>(
        WORKSPACE_USER_CACHE_KEYS.CHECK_DETAIL_PAGE_VIEW_CACHE_KEY,
        CheckDetailViewSchema,
    );
    const [view, setView] = useState<CheckDetailView>(
        cachedView ?? defaultView,
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            const parsed = CheckDetailViewSchema.safeParse(newView);
            if (parsed.success) {
                setView(parsed.data);
                setCachedView(parsed.data);
            }
        },
        [setCachedView],
    );

    const { download, isDownloading } = useCheckDetailDownload({
        filters,
        entityIds: selectedEntityIds,
        groupBy,
        columns: getCheckDetailVisibleColumns(view, config),
    });
    const downloadMenuState = getGeneralLedgerDownloadMenuState({
        isFetching: checkDetail.isFetching,
        isSuccess: checkDetail.isSuccess,
        postingRowCount: checkDetail.data
            ? [
                  ...checkDetail.data.groups,
                  ...checkDetail.data.depositGroups,
              ].reduce((total, group) => total + group.rows.length, 0)
            : 0,
    });

    const expandAllState = useMemo(
        () =>
            checkDetail.data
                ? getCheckDetailExpandedState(checkDetail.data, groupBy)
                : {},
        [checkDetail.data, groupBy],
    );
    const {
        expanded,
        setExpanded,
        isCollapsed,
        toggleAll: handleToggleCollapse,
    } = useExpandAllState(expandAllState);

    const handleEntityIdsChange = useCallback(
        (nextEntityIds: number[] | undefined) =>
            setFilters((current) => {
                const entityIds =
                    nextEntityIds && nextEntityIds.length > 0
                        ? nextEntityIds
                        : businessEntityIds;
                return {
                    ...current,
                    entityIds,
                    entityId:
                        entityIds.length === 1
                            ? entityIds[0]
                            : current.entityId,
                };
            }),
        [setFilters, businessEntityIds],
    );

    const handleGroupByChange = useCallback(
        (nextGroupBy: CheckDetailGroupBy | null) => {
            if (!nextGroupBy) {
                return;
            }
            setGroupByParam(
                nextGroupBy === "bankAccount" ? undefined : nextGroupBy,
            );
        },
        [setGroupByParam],
    );

    return (
        <>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                requireDateSelection
                hideLedgerSelect
                hideCreateJournalEntryButton
                showInvoiceAccountFilter
                accountFilterPredicate={isCheckingAccount}
                showCounterpartyFilter
                showAmountFilter
                showExclude1099Filter
                beforeEntityChildren={
                    <GroupBySelect
                        value={groupBy}
                        options={CHECK_DETAIL_GROUP_BY_OPTIONS}
                        onValueChange={handleGroupByChange}
                    />
                }
                entityFilterOverride={
                    <EntityFilterCombobox
                        filters={{ entityIds: selectedEntityIds }}
                        onlyBusiness
                        excludeAllMockEntities
                        onChange={(newFilters) =>
                            handleEntityIdsChange(newFilters.entityIds)
                        }
                    />
                }
                additionalButtons={
                    <>
                        <Tooltip
                            key={isCollapsed ? "expand" : "collapse"}
                            content={
                                isCollapsed ? "Expand all" : "Collapse all"
                            }
                        >
                            <Button
                                iconOnly
                                variant="primary"
                                size="md"
                                onClick={handleToggleCollapse}
                                aria-label={
                                    isCollapsed ? "Expand all" : "Collapse all"
                                }
                            >
                                {isCollapsed ? (
                                    <ExpandIcon />
                                ) : (
                                    <CollapseIcon />
                                )}
                            </Button>
                        </Tooltip>
                        <ColumnSettings
                            view={view}
                            onViewChange={handleViewChange}
                            viewConfig={config}
                            defaultView={defaultView}
                        />
                        <DownloadDropdown
                            onDownload={download}
                            disabled={downloadMenuState.menuDisabled}
                            loading={isDownloading}
                            pdfDisabled={downloadMenuState.pdfDisabled}
                            pdfDisabledTooltip={
                                GENERAL_LEDGER_PDF_TOO_LARGE_TOOLTIP
                            }
                        />
                    </>
                }
            />
            <LoaderOnInitialLoading queryResult={checkDetail} dimWhileFetching>
                {checkDetail.data && (
                    <CheckDetailContent
                        data={checkDetail.data}
                        groupBy={groupBy}
                        filters={reportFilters}
                        chrome={checkDetail.data.presentation.chrome}
                        numberFormat={
                            checkDetail.data.presentation.numberFormat
                        }
                        generationDate={generationDate}
                        expanded={expanded}
                        onExpandedChange={setExpanded}
                        view={view}
                        onViewChange={handleViewChange}
                        config={config}
                    />
                )}
            </LoaderOnInitialLoading>
        </>
    );
};
